/* eslint-disable react/jsx-props-no-spreading -- MUI Autocomplete's renderInput requires spreading its params */
import * as React from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import PageContainer from "../page-container/page-container";
import LoadingScreen from "../progress/loading-screen";
import useClients from "../client-config/data/use-clients";
import IngestionList from "./components/ingestion-list";
import SftpIngestionDialog from "./components/sftp-ingestion-dialog";
import EmailIngestionDialog from "./components/email-ingestion-dialog";
import useIngestionActions from "./hooks/use-ingestion-actions";
import {
  useEmailIngestions,
  useSftpIngestions,
} from "./hooks/use-ingestion-data";
import { EmailIngestion, IngestionKind, SftpIngestion } from "./types";
import { getEmailColumns, getSftpColumns } from "./utils/columns";

interface ClientOption {
  id: number;
  name: string;
}

type DialogState =
  | { kind: "sftp"; ingestion?: SftpIngestion }
  | { kind: "email"; ingestion?: EmailIngestion }
  | null;

// The count is left off until a tab's data has loaded, so "0" always means
// the client genuinely has none of that type.
const tabLabel = (name: string, rows: unknown[] | undefined) =>
  rows ? `${name} (${rows.length})` : name;

const ClientIngestion = () => {
  const {
    data: clients,
    isLoading: isLoadingClients,
    error: clientsError,
  } = useClients();
  const [selectedClientId, setSelectedClientId] = React.useState<number | null>(
    null,
  );
  const [activeTab, setActiveTab] = React.useState<IngestionKind>("sftp");
  const [dialog, setDialog] = React.useState<DialogState>(null);

  // SFTP and email are independent: each loads, fails and refreshes on its own.
  const sftp = useSftpIngestions(selectedClientId);
  const email = useEmailIngestions(selectedClientId);
  const { deleteSftpIngestion, deleteEmailIngestion } = useIngestionActions();
  const { mutate: mutateSftp } = sftp;
  const { mutate: mutateEmail } = email;

  const clientOptions = React.useMemo<ClientOption[]>(
    () =>
      (clients ?? [])
        .filter((client) => !client.deleted)
        .map((client) => ({ id: client.id, name: client.name })),
    [clients],
  );

  const selectedClient = React.useMemo(
    () =>
      clientOptions.find((option) => option.id === selectedClientId) ?? null,
    [clientOptions, selectedClientId],
  );

  const handleDeleteSftp = React.useCallback(
    async (ingestion: SftpIngestion) => {
      if (await deleteSftpIngestion(ingestion)) {
        mutateSftp();
      }
    },
    [deleteSftpIngestion, mutateSftp],
  );

  const handleDeleteEmail = React.useCallback(
    async (ingestion: EmailIngestion) => {
      if (await deleteEmailIngestion(ingestion)) {
        mutateEmail();
      }
    },
    [deleteEmailIngestion, mutateEmail],
  );

  const sftpColumns = React.useMemo(
    () =>
      getSftpColumns({
        onEdit: (ingestion) => setDialog({ kind: "sftp", ingestion }),
        onDelete: handleDeleteSftp,
      }),
    [handleDeleteSftp],
  );

  const emailColumns = React.useMemo(
    () =>
      getEmailColumns({
        onEdit: (ingestion) => setDialog({ kind: "email", ingestion }),
        onDelete: handleDeleteEmail,
      }),
    [handleDeleteEmail],
  );

  const handleDialogClose = (saved: boolean) => {
    if (saved) {
      if (dialog?.kind === "sftp") {
        mutateSftp();
      } else {
        mutateEmail();
      }
    }
    setDialog(null);
  };

  if (isLoadingClients) {
    return <LoadingScreen dataTestId="client-ingestion-loading" />;
  }

  const renderBody = () => {
    if (clientsError) {
      return <Alert severity="error">Error loading clients.</Alert>;
    }
    if (selectedClientId === null) {
      return (
        <Alert severity="info">
          Select a client to view and manage its SFTP and email ingestion.
        </Alert>
      );
    }
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(_event, value: IngestionKind) => setActiveTab(value)}
          >
            <Tab
              label={tabLabel("SFTP", sftp.data)}
              value="sftp"
              id="client-ingestion-tab-sftp"
              aria-controls="client-ingestion-tabpanel-sftp"
            />
            <Tab
              label={tabLabel("Email", email.data)}
              value="email"
              id="client-ingestion-tab-email"
              aria-controls="client-ingestion-tabpanel-email"
            />
          </Tabs>
        </Box>
        <Box
          role="tabpanel"
          id={`client-ingestion-tabpanel-${activeTab}`}
          aria-labelledby={`client-ingestion-tab-${activeTab}`}
          sx={{ flex: 1, display: "flex", flexDirection: "column", pt: 2 }}
        >
          {activeTab === "sftp" ? (
            <IngestionList
              kind="sftp"
              rows={sftp.data}
              isLoading={sftp.isLoading}
              error={sftp.error}
              columns={sftpColumns}
              onAdd={() => setDialog({ kind: "sftp" })}
            />
          ) : (
            <IngestionList
              kind="email"
              rows={email.data}
              isLoading={email.isLoading}
              error={email.error}
              columns={emailColumns}
              onAdd={() => setDialog({ kind: "email" })}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <PageContainer title="Client Ingestion">
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          id="client-ingestion-client-select"
          size="small"
          fullWidth
          options={clientOptions}
          value={selectedClient}
          onChange={(_event, option: ClientOption | null) =>
            setSelectedClientId(option?.id ?? null)
          }
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="No matching clients"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client"
              placeholder="Search clients"
            />
          )}
        />
      </Box>
      {renderBody()}
      {dialog?.kind === "sftp" && selectedClientId !== null ? (
        <SftpIngestionDialog
          open
          clientId={selectedClientId}
          ingestion={dialog.ingestion}
          onClose={handleDialogClose}
        />
      ) : null}
      {dialog?.kind === "email" && selectedClientId !== null ? (
        <EmailIngestionDialog
          open
          clientId={selectedClientId}
          ingestion={dialog.ingestion}
          onClose={handleDialogClose}
        />
      ) : null}
    </PageContainer>
  );
};

export default ClientIngestion;
